import { useEffect, useState } from "react";
import { api, post } from "../api";
import { ChargeBadge } from "../components/Attendance";
import { QuickSignInSettings } from "../components/QuickSignIn";
import { Notice, StudentShell } from "../components/Layout";
import { useI18n } from "../i18n";
import { passDisplayStatus, passStatusLabel } from "../pass-status";
import { dateInTimeZone } from "../time";
import type { MeResponse, Pass } from "../types";

export function MemberPage() {
  const { t, language } = useI18n();
  const [data, setData] = useState<MeResponse | null>(null); const [error, setError] = useState("");
  useEffect(() => { api<MeResponse>("/api/me").then(setData).catch(() => setError(t("error"))); }, [t]);
  async function logout() { await post("/api/student/logout", {}); window.location.replace("/"); }
  const visiblePasses = data?.passes.filter((pass) => pass.status !== "cancelled") ?? [];
  const unpaid = data?.payments.filter((payment) => payment.status === "unpaid") ?? [];
  const totalUnpaid = unpaid.reduce((sum, payment) => sum + payment.amount_minor, 0);
  return <StudentShell title={t("records")}><div className="member-stack">
    {error && <Notice kind="error">{error}</Notice>}
    {!data && !error && <div className="loading-card">{t("loading")}</div>}
    {data && <>
      <section className="member-card member-hero"><p>{data.student.displayName}</p><h2>{visiblePasses.length ? t("passStatus") : t("noPass")}</h2></section>
      <section className="member-card"><h2>{t("passStatus")}</h2>
        {visiblePasses.length ? visiblePasses.map((pass) => <PassLine key={pass.pass_id} pass={pass}
          today={dateInTimeZone(data.settings.timeZone)} language={language} />) : <p className="muted">{t("noPass")}</p>}
        {data.settings.renewalUrl && <a className="primary-link" href={data.settings.renewalUrl}>{t("renew")}</a>}
      </section>
      <section className="member-card"><h2>{t("unpaid")}</h2><p className="money">{data.settings.currency} {(totalUnpaid / 100).toFixed(2)}</p></section>
      <section className="member-card"><h2>{t("history")}</h2>{data.attendance.length ? data.attendance.map((row) =>
        <div className="record-line" key={row.attendance_id}>
          <strong>{row.title}</strong>
          <div className="record-meta"><span>{row.class_date}</span><span>{row.start_time}</span></div>
          <div className="row-actions"><ChargeBadge chargeType={row.charge_type} billingIntent={row.billing_intent} />
            {row.session_status === "cancelled" && <span className="status-badge status-cancelled">{t("cancelled")}</span>}
          </div>
        </div>
      ) : <p className="muted">{t("noRecords")}</p>}</section>
      <QuickSignInSettings />
      <a className="secondary-link" href="/">{t("back")}</a>
      <button className="text-button" onClick={logout}>{t("logout")}</button>
    </>}
  </div></StudentShell>;
}

function PassLine({ pass, today, language }: { pass: Pass; today: string; language: "zh" | "en" }) {
  const { t } = useI18n();
  const status = passDisplayStatus(pass, today);
  return <div className={"pass-line " + (pass.remaining_classes !== null && pass.remaining_classes <= 2 ? "warning" : "")}>
    <strong>{pass.pass_type === "unlimited" ? t("unlimited") : t("count")}</strong>
    <span className={`status-badge status-${status}`}>{passStatusLabel(status, language)}</span>
    <span>{t("remaining")}: {pass.pass_type === "unlimited" ? "∞" : pass.remaining_classes}</span>
    {status === "not_activated"
      ? <span>{language === "zh" ? `有效期：首次使用起計 ${pass.validity_days} 日` : `Validity: ${pass.validity_days} days from first check-in`}</span>
      : <span>{language === "zh" ? "最後可用日" : "Usable through"}: {pass.expiry_date || "—"}</span>}
  </div>;
}
